/**
 * Fetch + Blob download helper с правильным парсингом Content-Disposition.
 *
 * Главная задача — корректно вытащить кириллическое имя файла из header'а
 * `filename*=UTF-8''<percent-encoded>` (RFC 5987). Старый код брал только
 * ASCII-fallback `filename="..."`, куда backend (некорректно) клал
 * кириллицу как-есть — браузер декодировал её как latin1 и получался
 * mojibake вместо нормального имени.
 *
 * Сейчас backend кладёт ASCII-safe в `filename=` и кириллицу в
 * `filename*=UTF-8''...` — этот helper всегда предпочитает `filename*`.
 */

/** Извлечь имя файла из заголовка `Content-Disposition`. */
export function parseFilenameFromDisposition(
  contentDisposition: string | null | undefined,
): string | undefined {
  if (!contentDisposition) return undefined;
  // 1) RFC 5987 `filename*=UTF-8''<percent>`. Может содержать кавычки и
  //    различную капитализацию utf-8. Берём всё до точки с запятой.
  const utf8Match = contentDisposition.match(
    /filename\*\s*=\s*UTF-8''([^;]+)/i,
  );
  if (utf8Match) {
    try {
      // Сам percent-encoded payload может быть в кавычках (некоторые
      // прокси добавляют) — снимем их перед decode.
      const raw = utf8Match[1].trim().replace(/^"(.*)"$/, '$1');
      return decodeURIComponent(raw);
    } catch {
      // Невалидное URL-кодирование — выпадаем в ASCII fallback ниже.
    }
  }
  // 2) ASCII fallback `filename="..."`.
  const asciiMatch = contentDisposition.match(
    /filename\s*=\s*"?([^";]+)"?/i,
  );
  return asciiMatch?.[1];
}

interface DownloadOptions {
  /** Bearer-токен из Redux state — нужен потому что download-эндпоинты
   *  под `require_agent_access`. */
  accessToken: string | null | undefined;
  /** Fallback-имя если backend не прислал Content-Disposition. */
  fallbackName?: string;
}

/**
 * Скачать blob по URL (через fetch с Authorization-заголовком),
 * сохранить с правильным именем из `Content-Disposition` или fallback.
 *
 * Возвращает HTTP status — позволяет вызывающему коду показать
 * человекочитаемый баннер при ошибке.
 */
export async function downloadAuthorizedFile(
  url: string,
  opts: DownloadOptions,
): Promise<{ ok: boolean; status: number; error?: string }> {
  const resp = await fetch(url, {
    headers: opts.accessToken ? { Authorization: `Bearer ${opts.accessToken}` } : {},
  });
  if (!resp.ok) {
    return {
      ok: false,
      status: resp.status,
      error: `HTTP ${resp.status}`,
    };
  }
  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  const parsedName = parseFilenameFromDisposition(
    resp.headers.get('content-disposition'),
  );
  const fileName = parsedName ?? opts.fallbackName ?? 'download.bin';
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return { ok: true, status: resp.status };
}
